export type RiskTierNumber = 1 | 2 | 3 | 4 | 5 | 6;

export type ThesisTimeframe =
  | '1_day'
  | '1_2_weeks'
  | '1_month'
  | '6_months_plus';

export interface TierCatalogEntry {
  riskTier: RiskTierNumber;
  name: string;
  shortName: string;
  focus: string;
  targetReturnLabel: string;
  utilizationHint: string;
  defaultThesisTimeframe: ThesisTimeframe;
  /** Tailwind utility classes for chips and rail segments */
  chipClass: string;
  railClass: string;
  dotClass: string;
}

export const TIER_CATALOG: Record<RiskTierNumber, TierCatalogEntry> = {
  1: {
    riskTier: 1,
    name: 'Conservative / Macro ETF',
    shortName: 'T1',
    focus: 'Capital preservation, macro ETF sleeves, multi-year holds',
    targetReturnLabel: '4%',
    utilizationHint: '10%',
    defaultThesisTimeframe: '6_months_plus',
    chipClass: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    railClass: 'bg-emerald-500',
    dotClass: 'bg-emerald-500',
  },
  2: {
    riskTier: 2,
    name: 'Premium Harvest',
    shortName: 'T2',
    focus: 'Steady income via defined-risk options',
    targetReturnLabel: '8%',
    utilizationHint: '25%',
    defaultThesisTimeframe: '1_month',
    chipClass: 'bg-lime-50 text-lime-900 border-lime-200',
    railClass: 'bg-lime-500',
    dotClass: 'bg-lime-500',
  },
  3: {
    riskTier: 3,
    name: 'Balanced Growth',
    shortName: 'T3',
    focus: 'Credit spreads and neutral strategies',
    targetReturnLabel: '12%',
    utilizationHint: '40%',
    defaultThesisTimeframe: '1_month',
    chipClass: 'bg-teal-50 text-teal-900 border-teal-200',
    railClass: 'bg-teal-500',
    dotClass: 'bg-teal-500',
  },
  4: {
    riskTier: 4,
    name: 'Directional Alpha',
    shortName: 'T4',
    focus: 'Medium/high conviction directional trades',
    targetReturnLabel: '18%',
    utilizationHint: '60%',
    defaultThesisTimeframe: '1_2_weeks',
    chipClass: 'bg-sky-50 text-sky-900 border-sky-200',
    railClass: 'bg-sky-500',
    dotClass: 'bg-sky-500',
  },
  5: {
    riskTier: 5,
    name: 'Aggressive Leverage',
    shortName: 'T5',
    focus: 'High return with elevated drawdown tolerance',
    targetReturnLabel: '25%',
    utilizationHint: '80%',
    defaultThesisTimeframe: '1_day',
    chipClass: 'bg-orange-50 text-orange-900 border-orange-200',
    railClass: 'bg-orange-500',
    dotClass: 'bg-orange-500',
  },
  6: {
    riskTier: 6,
    name: 'Moonshot Options',
    shortName: 'T6',
    focus: 'Thesis-driven active options orchestration',
    targetReturnLabel: '1000%',
    utilizationHint: '95%',
    defaultThesisTimeframe: '1_2_weeks',
    chipClass: 'bg-violet-50 text-violet-900 border-violet-200',
    railClass: 'bg-violet-500',
    dotClass: 'bg-violet-500',
  },
};

export const ALL_RISK_TIER_NUMBERS: RiskTierNumber[] = [1, 2, 3, 4, 5, 6];

export function mapThesisTimeframeToRiskTiers(
  timeframe: ThesisTimeframe,
): RiskTierNumber[] {
  switch (timeframe) {
    case '6_months_plus':
      return [1];
    case '1_month':
      return [2, 3];
    case '1_2_weeks':
      return [3, 4, 6];
    case '1_day':
      return [4, 5, 6];
    default:
      return ALL_RISK_TIER_NUMBERS;
  }
}

export function getTierCatalogEntry(
  riskTier: number | undefined | null,
): TierCatalogEntry | null {
  if (riskTier == null || riskTier < 1 || riskTier > 6) return null;
  return TIER_CATALOG[riskTier as RiskTierNumber];
}
